import { Box, Grid, Typography } from "@mui/material";
import {
  Groups,
  ConfirmationNumber,
  Assignment,
  FactCheck,
  ReportProblem,
  ChangeCircle,
  CloudUpload,
} from "@mui/icons-material";
import { MonthlyData } from "../types";
import { TOYOTA_RED, INFO_BLUE, SUCCESS_GREEN, WARNING_ORANGE, ACCENT_BLUE, CHART_COLORS } from "../theme";
import KpiCard from "../components/KpiCard";
import PlotlyChart from "../components/PlotlyChart";
import ChartCard from "../components/ChartCard";

interface Props { data: MonthlyData; compact?: boolean; }

// How many engineers to show in distribution charts before they get noisy.
const MAX_ENGINEERS = 12;

const TeamProductivity = ({ data, compact }: Props) => {
  const tp = data.teamProductivity;

  if (!tp || tp.byEngineer.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "#94A3B8" }}>
        <Typography variant="body2">No team productivity data available for this month.</Typography>
      </Box>
    );
  }

  const { summary, byEngineer } = tp;
  const jira = summary.jiraAvailable;
  const hasEgress = summary.egressRequests !== null && summary.egressRequests !== undefined;
  const chartH = compact ? 300 : 360;

  const top = [...byEngineer].slice(0, MAX_ENGINEERS);
  const engineers = top.map((e) => e.engineer);

  // Category traces for the stacked work-distribution chart. Only categories with
  // a non-zero value across the shown engineers get a trace/legend entry.
  const allCategories: { name: string; field: keyof typeof byEngineer[number]; color: string }[] = [
    ...(hasEgress ? [{ name: "Egress", field: "egress" as const, color: ACCENT_BLUE }] : []),
    { name: "Data Requests", field: "dar", color: INFO_BLUE },
    { name: "Requests", field: "requests", color: SUCCESS_GREEN },
    { name: "Incidents", field: "incidents", color: TOYOTA_RED },
    { name: "Change Requests", field: "changeRequests", color: WARNING_ORANGE },
    ...(jira ? [{ name: "Jira", field: "jiraTickets" as const, color: CHART_COLORS[4] }] : []),
  ];
  const categories = allCategories.filter((cat) =>
    top.some((e) => Number(e[cat.field] ?? 0) > 0)
  );

  const distributionTraces = categories.map((cat) => ({
    y: engineers,
    x: top.map((e) => Number(e[cat.field] ?? 0)),
    name: cat.name,
    type: "bar" as const,
    orientation: "h" as const,
    marker: { color: cat.color },
  }));

  // Total labels at the tip of each stacked bar.
  const maxTotal = Math.max(1, ...top.map((e) => e.total));
  const totalAnnotations = top.map((e) => ({
    x: e.total,
    y: e.engineer,
    text: String(e.total),
    xanchor: "left" as const,
    yanchor: "middle" as const,
    showarrow: false,
    xshift: 6,
    font: { size: 11, color: "#0F172A" },
  }));

  return (
    <Box>
      {!compact && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "#1B2559" }}>
          👥 Team Productivity
        </Typography>
      )}

      {/* Summary KPIs */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={4}>
          <KpiCard label="Active Engineers" value={summary.activeEngineers} color={TOYOTA_RED} icon={<Groups />} />
        </Grid>
        {jira && (
          <Grid item xs={6} sm={4} md={4}>
            <KpiCard label="Jira" value={summary.jiraTickets} color={CHART_COLORS[4]} icon={<ConfirmationNumber />} />
          </Grid>
        )}
        {hasEgress && (
          <Grid item xs={6} sm={4} md={4}>
            <KpiCard label="Egress Requests" value={summary.egressRequests ?? 0} color={ACCENT_BLUE} icon={<CloudUpload />} />
          </Grid>
        )}
        <Grid item xs={6} sm={4} md={4}>
          <KpiCard label="Service Requests" value={summary.serviceRequests} color={INFO_BLUE} icon={<Assignment />} />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <KpiCard label="Data Requests" value={summary.darRequests} color={INFO_BLUE} icon={<FactCheck />} />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <KpiCard label="Incidents" value={summary.incidents} color={TOYOTA_RED} icon={<ReportProblem />} />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <KpiCard label="Change Requests" value={summary.changeRequests} color={WARNING_ORANGE} icon={<ChangeCircle />} />
        </Grid>
      </Grid>

      {/* Work distribution per engineer */}
      <ChartCard title="Work Distribution by Engineer">
        <PlotlyChart
          data={distributionTraces}
          layout={{
            barmode: "stack",
            height: Math.max(chartH, engineers.length * 28 + 80),
            margin: { t: 20, r: 40, b: 40, l: 140 },
            yaxis: { automargin: true, autorange: "reversed" },
            xaxis: { range: [0, maxTotal * 1.1] },
            annotations: totalAnnotations,
          }}
          height={Math.max(chartH, engineers.length * 28 + 80)}
        />
      </ChartCard>
    </Box>
  );
};

export default TeamProductivity;